<template>
    <div class="side-bar" :class="{ hidden: !expanded, expanding}">
        <div class="side-bar-body">
            <router-link :class="['side-bar-option',{ selected: page === 'home' }]" :title="$t('Home')" :to="{ name: 'home' }">
                <div class="side-bar-option-icon"><i class="fas fa-home"></i></div>
                <div class="side-bar-option-text">{{ $t("Home") }}</div>
            </router-link>

            <div class="side-bar-separator" v-if="canDataSources || canDataSinks "></div>

            <router-link
                v-if="canDataSources"
                class="side-bar-option"
                :class="{ selected: page === 'data-sources' || page === 'data-source' || page === 'data-sources-create' }"
                :title="$t('Data sources')"
                :to="{ name: 'data-sources' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-database"></i></div>
                <div class="side-bar-option-text">{{ $t("Data sources") }}</div>
            </router-link>

            <router-link
                v-if="canDataSinks"
                class="side-bar-option"
                :class="{ selected: page === 'data-sinks' || page === 'data-sink' || page === 'data-sinks-create' }"
                :title="$t('Data sinks')"
                :to="{ name: 'data-sinks' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-database"></i></div>
                <div class="side-bar-option-text">{{ $t("Data sinks") }}</div>
            </router-link>

            <router-link
                v-if="canPipelines"
                class="side-bar-option"
                :class="{ selected: page === 'pipelines' || page === 'pipeline' || page === 'pipeline-create' || page === 'execution' }"
                :title="$t('Data pipelines')"
                :to="{ name: 'pipelines' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-project-diagram"></i></div>
                <div class="side-bar-option-text">{{ $t("Data pipelines") }}</div>
            </router-link>


            <div class="side-bar-separator" v-if="canReadCatalogs"></div>

            <router-link
                v-if="canReadCatalogs"
                class="side-bar-option"
                :class="{ selected: page === 'catalogs' || page === 'catalog' || page === 'catalog-create' }"
                :title="$t('Catalogs')"
                :to="{ name: 'catalogs' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-layer-group"></i></div>
                <div  class="side-bar-option-text">{{ $t("Catalogs") }}</div>
            </router-link>

            <router-link
                v-if="canReadCatalogs"
                class="side-bar-option"
                :class="{ selected: page === 'datasets' || page === 'dataset' || page === 'dataset-create' }"
                :title="$t('Datasets')"
                :to="{ name: 'datasets' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-table"></i></div>
                <div class="side-bar-option-text">{{ $t("Datasets") }}</div>
            </router-link>

            <div class="side-bar-separator" v-if="canReadNegotiations"></div>

            <router-link
                v-if="canReadNegotiations"
                class="side-bar-option"
                :class="{ selected: page === 'negotiations' || page === 'negotiation' || page === 'negotiation-create' }"
                :title="$t('Negotiations')"
                :to="{ name: 'negotiations' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-handshake"></i></div>
                <div class="side-bar-option-text">{{ $t("Negotiations") }}</div>
            </router-link>

            <router-link
                v-if="canReadNegotiations"
                class="side-bar-option"
                :class="{ selected: page === 'agreements' || page === 'agreement' }"
                :title="$t('Agreements')"
                :to="{ name: 'agreements' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-thumbs-up"></i></div>
                <div class="side-bar-option-text">{{ $t("Agreements") }}</div>
            </router-link>

            <div class="side-bar-separator" v-if="canReadClearingHouse"></div>

            <router-link
                v-if="canReadClearingHouse"
                class="side-bar-option"
                :class="{ selected: page === 'clearing-house' || page === 'clearing-house-agreement' }"
                :title="$t('Clearing house')"
                :to="{ name: 'clearing-house' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-scale-balanced"></i></div>
                <div class="side-bar-option-text">{{ $t("Clearing house") }}</div>
            </router-link>

            <div class="side-bar-separator" v-if="canReadTransfers"></div>

            <router-link
                v-if="canReadTransfers"
                class="side-bar-option"
                :class="{ selected: page === 'transfers' || page === 'transfer' || page === 'transfer-create' }"
                :title="$t('Transfers')"
                :to="{ name: 'transfers' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-right-left"></i></div>
                <div class="side-bar-option-text">{{ $t("Transfers") }}</div>
            </router-link>

            <div class="side-bar-separator" v-if="canDataSpaceConfig"></div>

            <router-link
                v-if="canDataSpaceConfig"
                class="side-bar-option"
                :class="{ selected: page === 'admin-data-space' }"
                :title="$t('Data space configuration')"
                :to="{ name: 'admin-data-space' }"
            >
                <div class="side-bar-option-icon"><i class="fas fa-cog"></i></div>
                <div class="side-bar-option-text">{{ $t("Data space configuration") }}</div>
            </router-link>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "../../utils/v-model";
import { AuthController } from "@/control/auth";

export default defineComponent({
    components: {},
    name: "MenuSideBar",
    emits: ["update:expanded"],
    props: {
        expanded: Boolean,
    },
    setup(props) {
        return {
            expandedStatus: useVModel(props, "expanded"),
        };
    },
    data: function () {
        return {
            page: "",
            expanding: false,
            canDataSpaceConfig: ["admin.gateway", "admin.wallets", "catalog.admin"].map(AuthController.hasPermission).includes(true),
            canDataSources: ["sources.read"].map(AuthController.hasPermission).includes(true),
            canDataSinks: ["sinks.read"].map(AuthController.hasPermission).includes(true),
            canPipelines: ["pipelines.read"].map(AuthController.hasPermission).includes(true),
            canReadCatalogs: ["catalog.read"].map(AuthController.hasPermission).includes(true),
            canReadNegotiations: ["negotiation.read"].map(AuthController.hasPermission).includes(true),
            canReadTransfers: ["transfer.read"].map(AuthController.hasPermission).includes(true),
            canReadClearingHouse: AuthController.ClearingHouseEnabled && ["clearing.house.read"].map(AuthController.hasPermission).includes(true),
        };
    },
    methods: {
        updatePage: function () {
            this.page = this.$route ? (this.$route.name as string) : "";
        },

        onAuthChanged: function () {
            this.canDataSpaceConfig = ["admin.gateway", "admin.wallets", "catalog.admin", "admin.payments"].map(AuthController.hasPermission).includes(true);
            this.canDataSources = ["sources.read"].map(AuthController.hasPermission).includes(true);
            this.canDataSinks = ["sinks.read"].map(AuthController.hasPermission).includes(true);
            this.canPipelines = ["pipelines.read"].map(AuthController.hasPermission).includes(true);
            this.canReadCatalogs = ["catalog.read"].map(AuthController.hasPermission).includes(true);
            this.canReadNegotiations = ["negotiation.read"].map(AuthController.hasPermission).includes(true);
            this.canReadTransfers = ["transfer.read"].map(AuthController.hasPermission).includes(true);
            this.canReadClearingHouse = AuthController.ClearingHouseEnabled && ["clearing.house.read"].map(AuthController.hasPermission).includes(true);
        },
    },
    mounted: function () {
        this.updatePage();

        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
    },
    watch: {
        $route: function () {
            this.updatePage();
        },
        expanded: function (){
            this.expanding = true
            setTimeout(() => {
                this.expanding = false
            }, 200);
        }
    },
});
</script>

<style></style>
